import { colors, radius, shadowM, spacing } from '@ardoq/design-tokens';
import styled from 'styled-components';
import {
  DrawerOrientation,
  DrawerWrapperProps,
  ModalDrawerSize,
} from './types';
import { forwardRef, PropsWithChildren } from 'react';

const getHeightOfDrawer = (
  drawerSize: ModalDrawerSize,
  orientation: DrawerOrientation
) => {
  if (orientation === 'vertical') return '100vh';

  if (drawerSize === 'small') return '49vh';
  if (drawerSize === 'medium') return '67vh';
  return '85vh';
};

const DEFAULT_MIN_WIDTH_PX = 480;

const getWidthOfDrawer = ({
  drawerSize,
  orientation,
  width,
}: {
  drawerSize: ModalDrawerSize;
  orientation: DrawerOrientation;
  width: number | undefined;
}) => {
  if (width !== undefined) return `${width}px`;
  if (orientation === 'horizontal') return '100vw';

  if (drawerSize === 'small') return '20vw';
  if (drawerSize === 'medium') return '67vw';
  return '85vw';
};

const getBorderRadius = (
  orientation: DrawerOrientation,
  hasSquareCorner: boolean
) => {
  if (hasSquareCorner) return '0px';
  return orientation === 'vertical'
    ? `${radius.r16} 0px 0px ${radius.r16}`
    : `${radius.r16} ${radius.r16} 0px 0px`;
};

const DrawerWrapperStyle = styled.div<{
  $drawerSize: ModalDrawerSize;
  $orientation: DrawerOrientation;
  $hasSquareCorner: boolean;
  $width: number | undefined;
}>`
  display: flex;
  flex-direction: column;
  border-radius: ${({ $orientation, $hasSquareCorner }) =>
    getBorderRadius($orientation, $hasSquareCorner)};
  box-shadow: ${shadowM};
  overflow: hidden;
  background-color: ${colors.surfaceDefault};
  width: ${({ $drawerSize, $orientation, $width }) =>
    getWidthOfDrawer({
      drawerSize: $drawerSize,
      orientation: $orientation,
      width: $width,
    })};
  min-height: 240px;
  min-width: ${({ $width }) => `${$width ?? DEFAULT_MIN_WIDTH_PX}px`};
  gap: ${spacing.s24};
  height: ${({ $drawerSize, $orientation }) =>
    getHeightOfDrawer($drawerSize, $orientation)};
`;

const DrawerWrapper = forwardRef<
  HTMLDivElement,
  PropsWithChildren<DrawerWrapperProps>
>(
  (
    {
      drawerSize = 'medium',
      orientation = 'horizontal',
      dataTourId,
      hasSquareCorner = false,
      width,
      children,
    },
    ref
  ) => (
    <DrawerWrapperStyle
      $drawerSize={drawerSize}
      $orientation={orientation}
      $hasSquareCorner={hasSquareCorner}
      $width={width}
      data-tour-id={dataTourId}
      ref={ref}
    >
      {children}
    </DrawerWrapperStyle>
  )
);

export { DrawerWrapper };
